import { Box, Card, Stack, Chip } from '@mui/material';
import { useState } from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  useTranslate,
  CreateButton,
  DeleteButton,
  Button,
  SelectInput,
  EditButton,
  BooleanField,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { Resources } from '../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import XDateField from '../../components/XDateField';
import AuditLogDialog from '../../components/AuditLog/AuditLogDialog';
import { Iconify } from 'src/components/iconify';
import { XButton } from '../../components/XButton';
import FilterGroup from '../../components/FilterGroup';
import XTextInput from '../../components/XTextInput';
import IdField from '../../components/IdField';
import { EmptyContent } from 'src/components/empty-content';
import { SCOPE_CHOICES, LEVEL_CHOICES, SOURCE_CHOICES } from './constants';
import { AUDIT_RESOURCE, PERMISSION_RESOURCE } from './utils';

const View = () => {
  const translate = useTranslate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const listFilters = [
    <FilterGroup alwaysOn direction="row">
      <XTextInput source="search" label={translate('module.announcementPopup.searchPlaceholder')} />
      <SelectInput
        source="scope"
        label={translate('module.announcementPopup.scope')}
        emptyText="ALL"
        choices={SCOPE_CHOICES.map((c) => ({ id: c.id, name: translate(c.name) }))}
        sx={{ marginBottom: '0' }}
      />
      <SelectInput
        source="level"
        label={translate('module.announcementPopup.level')}
        emptyText="ALL"
        choices={LEVEL_CHOICES.map((c) => ({ id: c.id, name: translate(c.name) }))}
        sx={{ marginBottom: '0' }}
      />
      <SelectInput
        source="source"
        label={translate('module.announcementPopup.source')}
        emptyText="ALL"
        choices={SOURCE_CHOICES.map((c) => ({ id: c.id, name: translate(c.name) }))}
        sx={{ marginBottom: '0' }}
      />
    </FilterGroup>,
  ];

  return (
    <PermissionGuard resource={PERMISSION_RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.announcementPopup.title')}
          links={[
            {
              name: translate('module.announcementPopup.title'),
              href: `/${Resources.announcementPopup}`,
            },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <PermissionGuard resource={PERMISSION_RESOURCE} action="create" fallback={<></>}>
                <CreateButton variant="contained" size="medium" />
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setSelectedRecord({});
                    setAuditTitle(
                      `${translate('resource.auditLogs')} - ${translate('resource.announcementPopup')}`
                    );
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            filters={listFilters}
            perPage={10}
            exporter={false}
            resource={Resources.announcementPopup}
            sort={{ field: 'createdAt', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                empty={<EmptyContent />}
                rowClick={(id, _resource, record) => (record?.source === 'auto' ? false : 'edit')}
                bulkActionButtons={false}
                sx={{
                  '& .RaDatagrid-thead th, & .RaDatagrid-tbody td': {
                    whiteSpace: 'nowrap',
                    padding: '8px 12px',
                    verticalAlign: 'middle',
                  },
                }}
              >
                <FunctionField
                  label={translate('module.announcementPopup.id')}
                  render={(record) => <IdField id={record.id} />}
                />
                <TextField source="code" label={translate('module.announcementPopup.bannerId')} />
                <FunctionField
                  label={translate('module.announcementPopup.scope')}
                  render={(record: any) => {
                    const config = SCOPE_CHOICES.find((c) => c.id === record.scope);
                    return (
                      <Chip
                        size="small"
                        label={config ? translate(config.name) : record.scope}
                        sx={{ color: config?.color, fontWeight: 600 }}
                      />
                    );
                  }}
                />
                <FunctionField
                  label={translate('module.announcementPopup.level')}
                  render={(record: any) => {
                    const config = LEVEL_CHOICES.find((c) => c.id === record.level);
                    return (
                      <Chip
                        size="small"
                        label={config ? translate(config.name) : record.level}
                        color={(config?.color as any) || 'default'}
                      />
                    );
                  }}
                />
                <FunctionField
                  label={translate('module.announcementPopup.source')}
                  render={(record: any) => {
                    const config = SOURCE_CHOICES.find((c) => c.id === record.source);
                    return (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={config ? translate(config.name) : record.source}
                        color={record.source === 'manual' ? 'primary' : 'default'}
                      />
                    );
                  }}
                />
                <BooleanField source="enabled" label={translate('module.announcementPopup.enabled')} />
              {/*   <FunctionField
                  label={translate('module.announcementPopup.messageZh')}
                  render={(record: any) => (
                    <Box
                      sx={{
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {record?.message?.zh  || '-'}
                    </Box>
                  )}
                /> */}

                <FunctionField
                  label={translate('module.announcementPopup.messageEn')}
                  render={(record: any) => (
                    <Box
                      sx={{
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {record?.message?.en || '-'}
                    </Box>
                  )}
                />
                <FunctionField
                  label={translate('module.announcementPopup.startAt')}
                  render={(record) => <XDateField value={record.startAt} fullDate />}
                />
                <FunctionField
                  label={translate('module.announcementPopup.endAt')}
                  render={(record) => <XDateField value={record.endAt} fullDate />}
                />
                <FunctionField
                  label={translate('module.announcementPopup.createdAt')}
                  render={(record) => <XDateField value={record.createdAt} fullDate />}
                />
                <FunctionField
                  render={(record: any) => (
                    <Stack direction="row" spacing={2} onClick={(e) => e.stopPropagation()}>
                      <>
                          
                        <PermissionGuard resource={PERMISSION_RESOURCE} action="update" fallback={<></>}>
                          <EditButton />
                        </PermissionGuard>
                        {
                          record.source !== 'auto' &&  
                          <PermissionGuard resource={PERMISSION_RESOURCE} action="delete" fallback={<></>}>
                            <DeleteButton mutationMode="pessimistic" />
                          </PermissionGuard>
                        }
                        
                       
                      </>
                      <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                        <XButton
                          variant="text"
                          size="small"
                          color="secondary"
                          onClick={() => {
                            setAuditTitle(`Audit Logs - ${record.id}`);
                            setSelectedRecord(record);
                            setAuditLogOpen(true);
                          }}
                          label={translate('resource.auditLogs')}
                        >
                          <Iconify icon="ph:note" />
                        </XButton>
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          recordId={selectedRecord?.id}
          resource={AUDIT_RESOURCE}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
